import { aiGraphView } from '@/features/backtest/utils/ai-graph-view'
import { store } from '@/store'
import { edgeBacktestApi } from '@/services/api/backtest-api'
import type { GraphEdge, GraphNode, Strategy } from '@/features/backtest/schemas/backtest-types'
import { applyGraphOps } from '@/features/backtest/graph-engine/graph-ops'
import type { GraphOp } from '@/features/backtest/graph-engine/graph-ops'
import type { EngineError } from '@/features/backtest/graph-engine/types'
import { computeGraphReadiness } from '@/features/backtest/hooks/use-graph-readiness'
import { graphToRecipe } from '@/features/backtest/graph-engine/graph-to-recipe'
import { buildGraphFromRecipe } from '@/features/backtest/utils/recipe-builder'
import type { Recipe } from '@/features/backtest/utils/recipe-types'
import { validateGraphBeforeBacktest } from '@/features/backtest/utils/validate-graph'
import type { Edge, Node } from '@xyflow/react'
import type { NodeData } from '@/features/backtest/types/nodes'
import { buildEngineStrategyGraph } from '@/features/backtest/utils/engine-view'

export interface CreateSavedStrategyInput {
  name: string
  recipe: Recipe
  description?: string
  tags?: string[]
}

export interface UpdateSavedStrategyInput {
  id: string
  nodes: GraphNode[]
  edges: GraphEdge[]
  expectedGraphRevision: number
}

export interface HeadlessGraphApi {
  getStrategy: (strategyId: string) => Promise<Strategy>
  createStrategy: (input: {
    name: string
    description?: string
    nodes: GraphNode[]
    edges: GraphEdge[]
    tags?: string[]
  }) => Promise<Strategy>
  updateStrategy: (input: UpdateSavedStrategyInput) => Promise<Strategy>
}

export interface SavedGraphOpsResult {
  strategy: Strategy
  idMap: Record<string, string>
  warnings: EngineError[]
  /** The graph the edit replaced, so the AI's change can be undone. */
  previous: { nodes: GraphNode[]; edges: GraphEdge[] }
}

export class HeadlessGraphValidationError extends Error {
  readonly errors: EngineError[]

  constructor(errors: EngineError[]) {
    super(errors.map((error) => error.message).join('; '))
    this.name = 'HeadlessGraphValidationError'
    this.errors = errors
  }
}

export class GraphRevisionConflictError extends Error {
  readonly expectedGraphRevision: number
  readonly actualGraphRevision?: number

  constructor(expectedGraphRevision: number, actualGraphRevision?: number, cause?: unknown) {
    super(
      actualGraphRevision === undefined
        ? `Strategy graph revision conflict (expected ${expectedGraphRevision})`
        : `Strategy graph revision conflict (expected ${expectedGraphRevision}, found ${actualGraphRevision})`,
    )
    this.name = 'GraphRevisionConflictError'
    this.expectedGraphRevision = expectedGraphRevision
    this.actualGraphRevision = actualGraphRevision
    if (cause !== undefined) this.cause = cause
  }
}

export function isGraphRevisionConflict(error: unknown): boolean {
  if (error instanceof GraphRevisionConflictError) return true
  if (!error || typeof error !== 'object') return false
  const value = error as { status?: unknown; data?: { status?: unknown; code?: unknown } }
  return (
    value.status === 409 ||
    value.data?.status === 409 ||
    value.data?.code === 'GRAPH_REVISION_CONFLICT'
  )
}

/** The ids the model reads (utils/ai-graph-view.ts), to the canvas nodes they name. */
function readIdMap(nodes: GraphNode[], edges: GraphEdge[]): Map<string, string> {
  const view = aiGraphView(nodes, edges)
  return graphToRecipe(view.nodes, view.edges).idMap
}

function assertRunnableGraph(nodes: GraphNode[], edges: GraphEdge[], requireReady = false): void {
  if (nodes.length === 0) {
    throw new HeadlessGraphValidationError([
      { code: 'INVALID_REF', message: 'Strategy graph must contain at least one node' },
    ])
  }
  // What the engine runs, not the raw canvas graph: subnet frames and pills
  // have no node schema, so validating the raw graph refused every strategy
  // holding a subnet (audit 2026-10-06). The editor's readiness reads the
  // same view.
  const report = computeGraphReadiness(
    nodes as unknown as Node<NodeData>[],
    edges as unknown as Edge[],
  )
  if (report.errors.length > 0) throw new HeadlessGraphValidationError(report.errors)
  if (requireReady) {
    const readiness = validateGraphBeforeBacktest(
      nodes as unknown as Node<NodeData>[],
      edges as unknown as Edge[],
    )
    const missingEntry = report.warnings.filter((warning) => warning.code === 'MISSING_ENTRY_RULES')
    if (readiness.length || missingEntry.length) {
      throw new HeadlessGraphValidationError([
        ...missingEntry,
        ...readiness.map((error) => ({
          code: 'UNCONNECTED_REQUIRED_INPUT' as const,
          stepId: error.nodeId,
          message: `${error.nodeLabel}: ${error.missingInputs.join(', ')}`,
        })),
      ])
    }
    if (
      !buildEngineStrategyGraph(nodes as unknown as Node[], edges as unknown as Edge[]).nodes.length
    ) {
      throw new HeadlessGraphValidationError([
        { code: 'INVALID_REF', message: 'Strategy has no runnable nodes.' },
      ])
    }
  }
}

export function createHeadlessGraphService(api: HeadlessGraphApi) {
  return {
    async createSavedStrategyFromRecipe(input: CreateSavedStrategyInput): Promise<Strategy> {
      if (!input.name.trim() || input.name.length > 120) {
        throw new Error('Strategy name must be between 1 and 120 characters')
      }
      const built = buildGraphFromRecipe(input.recipe)
      if (!built.success) {
        throw new HeadlessGraphValidationError(
          built.engineErrors ?? built.errors.map((message) => ({ code: 'INVALID_REF', message })),
        )
      }
      assertRunnableGraph(built.nodes, built.edges, true)
      return api.createStrategy({
        name: input.name,
        description: input.description,
        tags: input.tags,
        nodes: built.nodes,
        edges: built.edges,
      })
    },

    async readSavedStrategyGraph(strategyId: string): Promise<Strategy> {
      if (!strategyId) throw new Error('strategyId is required')
      return api.getStrategy(strategyId)
    },

    async applySavedGraphOps(input: {
      strategyId: string
      expectedGraphRevision: number
      ops: GraphOp[]
      idMap?: Record<string, string>
    }): Promise<SavedGraphOpsResult> {
      const current = await this.readSavedStrategyGraph(input.strategyId)
      if (current.script || current.uiState?.editorMode === 'code')
        throw new Error('Code-mode strategies cannot be edited with saved graph tools.')
      const currentRevision = current.graphRevision ?? 0
      if (currentRevision !== input.expectedGraphRevision) {
        throw new GraphRevisionConflictError(input.expectedGraphRevision, currentRevision)
      }
      if (input.ops.length === 0) throw new Error('ops (non-empty array) is required')

      const result = applyGraphOps(
        { nodes: current.nodes, edges: current.edges },
        input.ops,
        new Map([...readIdMap(current.nodes, current.edges), ...Object.entries(input.idMap ?? {})]),
      )
      if (!result.ok) throw new HeadlessGraphValidationError(result.errors)
      assertRunnableGraph(result.value.graph.nodes, result.value.graph.edges)

      let saved: Strategy
      try {
        saved = await api.updateStrategy({
          id: input.strategyId,
          nodes: result.value.graph.nodes,
          edges: result.value.graph.edges,
          expectedGraphRevision: input.expectedGraphRevision,
        })
      } catch (error) {
        if (isGraphRevisionConflict(error)) {
          throw new GraphRevisionConflictError(input.expectedGraphRevision, undefined, error)
        }
        throw error
      }

      return {
        strategy: saved,
        idMap: Object.fromEntries(result.value.idMap),
        warnings: result.value.warnings,
        previous: { nodes: current.nodes, edges: current.edges },
      }
    },

    /**
     * Write an earlier graph back (2026-10-15: Undo for an AI edit). Guarded by
     * the revision the edit produced, so an edit made since is never
     * overwritten; a conflict is the caller's to report.
     */
    async restoreSavedGraph(input: {
      strategyId: string
      nodes: GraphNode[]
      edges: GraphEdge[]
      expectedGraphRevision: number
    }): Promise<Strategy> {
      return api.updateStrategy({
        id: input.strategyId,
        nodes: input.nodes,
        edges: input.edges,
        expectedGraphRevision: input.expectedGraphRevision,
      })
    },
  }
}

const api: HeadlessGraphApi = {
  getStrategy: (strategyId) =>
    store
      .dispatch(edgeBacktestApi.endpoints.getStrategy.initiate(strategyId, { forceRefetch: true }))
      .unwrap(),
  createStrategy: (input) =>
    store.dispatch(edgeBacktestApi.endpoints.createStrategy.initiate(input)).unwrap(),
  updateStrategy: (input) =>
    store.dispatch(edgeBacktestApi.endpoints.updateStrategy.initiate(input)).unwrap(),
}

const defaultService = createHeadlessGraphService(api)

export const createSavedStrategyFromRecipe = defaultService.createSavedStrategyFromRecipe
export const readSavedStrategyGraph = defaultService.readSavedStrategyGraph
export const applySavedGraphOps = defaultService.applySavedGraphOps
export const restoreSavedGraph = defaultService.restoreSavedGraph
